import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import '../App.css';

const Help: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            Stream Signal
          </Link>
          <button 
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {user ? (
              <>
                <span className="nav-user">Welcome, {user.username}</span>
                <Link to="/help" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Help
                </Link>
                <button onClick={toggleDarkMode} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <button onClick={handleLogout} className="nav-logout">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/help" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Help
                </Link>
                <button onClick={toggleDarkMode} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <Link to="/login" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
                <Link to="/register" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <header className="App-header">
        <h1>Help & Setup Guide</h1>
        <p>Everything you need to know about Stream Signal</p>
      </header>

      <main className="main-content">
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'left', padding: '0 20px' }} className="help-content">
        
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>📱 What is Stream Signal?</h2>
          <p style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            Stream Signal is a physical notification system that uses LED lights to alert you when your 
            favorite Twitch or YouTube streamers go live. Perfect for channel moderators and super fans 
            who want instant awareness without smartphone notification clutter.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🚀 Getting Started</h2>
          <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Register an account</strong> - <Link to="/register">Create your account</Link> to get started</li>
            <li><strong>Add your device</strong> - Enter your device's unique hash to connect it to your account</li>
            <li><strong>Configure streams</strong> - Add the channels you want to monitor with custom colors</li>
            <li><strong>Let it run</strong> - Your device will automatically light up when streams go live!</li>
          </ol>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🛠️ First-time Device Setup</h2>
          <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Connect the device to a USB cable.</li>
            <li>Connect to the device Wi‑Fi network with SSID "AC-ESP8266" and password "987654321".</li>
            <li>After connecting to that Wi‑Fi, open a web browser and navigate to <strong>192.168.4.22</strong>.</li>
            <li>On that page, enter your home Wi‑Fi's SSID in the <em>WIFI Name</em> field and the Wi‑Fi password in the <em>password</em> field.</li>
            <li>Click "Save".</li>
            <li>The device will reboot and the connection to the "AC-ESP8266" network should disappear.</li>
            <li>Now add your device on the Devices page using the provided token (your device comes with this token).</li>
            <li>You're all set — your device should now connect to your home Wi‑Fi and receive stream updates.</li>
          </ol>

          <p style={{ marginTop: 12, fontStyle: 'italic' }}>
            <strong>Important:</strong> If you have mobile data enabled on your phone it can interfere with connecting to the device's configuration Wi‑Fi. Disable mobile data while configuring the device if you encounter connectivity issues.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🔧 Adding Your Device</h2>
          <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Obtain your device hash (provided with your hardware)</li>
            <li>Go to the home page and enter the device hash in the "Add New Device" section</li>
            <li>Click "Add Device" to link it to your account</li>
            <li><strong>Rename your device</strong> - Double-click on the device tab to give it a custom name</li>
          </ol>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🎨 Configuring Streams</h2>
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Adding a Stream</h3>
          <ol style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '16px' }}>
            <li>Select a device from the tabs</li>
            <li>Choose the platform (Twitch or YouTube)</li>
            <li>Enter the channel ID</li>
            <li>Pick a color that will light up when this streamer goes live</li>
            <li>Toggle "Blink" if you want the LED to blink for this stream</li>
            <li>Click "Preview" to test the color on your device before adding</li>
            <li>Click "Add Stream" to save</li>
          </ol>
          <p style={{ marginLeft: '20px', fontStyle: 'italic' }}><strong>Note:</strong> Each device can have a maximum of 5 streams configured.</p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>⚡ Priority System</h2>
          <p style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            When multiple streams are live at the same time, Stream Signal uses a priority system 
            to determine which color to display on your device.
          </p>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Drag and drop</strong> streams to reorder them</li>
            <li>Streams at the <strong>top</strong> have <strong>higher priority</strong></li>
            <li>The numbered badge (1, 2, 3...) shows each stream's priority rank</li>
            <li>When multiple streams are live, the device shows the color of the highest priority stream</li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🔕 Muting Streams</h2>
          <p style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            Need to temporarily ignore a stream without deleting it?
          </p>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Click the "🔕 Mute" button on any stream</li>
            <li>Muted streams are dimmed and show a "MUTED" badge</li>
            <li>Muted streams are completely ignored by the checker</li>
            <li>Click "🔔 Unmute" to resume notifications</li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>👁️ Preview Feature</h2>
          <p style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            Test your color choices before saving:
          </p>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Select a color and toggle blink settings</li>
            <li>Click the "👁️ Preview" button</li>
            <li>Your device will temporarily display the selected color</li>
            <li>This helps you find the perfect color for each streamer</li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>🎯 Channel IDs</h2>
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Finding Twitch Channel IDs</h3>
          <p style={{ marginBottom: '16px', marginLeft: '20px', lineHeight: '1.6' }}>
            Use the channel's username (e.g., "shroud", "pokimane")
          </p>
          
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Finding YouTube Channel IDs</h3>
          <p style={{ marginBottom: '12px', marginLeft: '20px', lineHeight: '1.6' }}>
            Look for the channel ID in the URL:
          </p>
          <ul style={{ marginLeft: '40px', lineHeight: '1.8' }}>
            <li>New format: <code>youtube.com/@username</code> - Use the @username</li>
            <li>Old format: <code>youtube.com/channel/UC...</code> - Use the UC... ID</li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ marginBottom: '16px' }}>❓ Troubleshooting</h2>
          
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Device not lighting up?</h3>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '20px' }}>
            <li>Verify your device is powered on and connected to the internet</li>
            <li>Use the Preview button to test connectivity</li>
            <li>Check that the stream is not muted</li>
          </ul>
          
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Wrong streamer showing?</h3>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '20px' }}>
            <li>Adjust priority by dragging streams to reorder them</li>
            <li>Remember: Top stream = Highest priority</li>
          </ul>
          
          <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>Too many notifications?</h3>
          <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Use the Mute feature to temporarily disable specific streams</li>
            <li>Or adjust priorities so less important streams are lower</li>
          </ul>
        </section>

        <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
          <Link to="/" style={{ fontSize: '16px', textDecoration: 'none', color: '#667eea', fontWeight: '500' }}>
            ← Back to Home
          </Link>
        </div>
        </div>
      </main>
    </div>
  );
};

export default Help;
